import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import Spinner from '../common/Spinner/Spinner';
import { FaInfoCircle } from 'react-icons/fa';
import { SearchableSelectBase } from '../common/SearchableSelect/SearchableSelect';
import { calcularMediaGeral } from '../../utils/diagnosticoStats';
import './PublicOrgShowcase.css';

const PublicOrgShowcase = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const q = query(
          collection(db, 'organizacoes'),
          where('diagnosticoPublico', '==', true)
        );
        const snap = await getDocs(q);
        const list = await Promise.all(snap.docs.map(async (doc) => {
          const data = doc.data();
          const orgId = doc.id;
          let calculatedMediaGeral = 'N/A';

          // If mediaGeral is not directly available or not a number, calculate it
          if (typeof data.mediaGeral !== 'number') {
            try {
              const questoesSnap = await getDocs(collection(db, 'questoes'));
              const respostasSnap = await getDocs(
                query(collection(db, 'respostas'), where('idOrganizacao', '==', orgId))
              );

              const todasQuestoes = questoesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
              const respostasBrutas = respostasSnap.docs.map(d => d.data());

              const respostasUnicasMap = new Map();
              for (const resposta of respostasBrutas) {
                if (resposta.idQuestao) {
                  const entradaExistente = respostasUnicasMap.get(resposta.idQuestao);
                  if (!entradaExistente || resposta.resposta_principal) {
                    respostasUnicasMap.set(resposta.idQuestao, resposta);
                  }
                }
              }
              const respostasLimpa = Array.from(respostasUnicasMap.values());

              if (todasQuestoes.length > 0) { // Calculate based on total questions
                calculatedMediaGeral = calcularMediaGeral(todasQuestoes, respostasLimpa).toFixed(1);
              }
            } catch (calcErr) {
              console.error('Erro ao calcular mediaGeral para', data.nome, ':', calcErr);
              calculatedMediaGeral = 'N/A'; // Fallback in case of calculation error
            }
          } else {
            // O valor do banco de dados é uma fração (0-1), então convertemos para percentual.
            calculatedMediaGeral = (data.mediaGeral * 100).toFixed(1);
          }

          return {
            id: orgId,
            nome: data.nome,
            logoUrl: data.logoUrl || null,
            diagnosticoPublico: Boolean(data.diagnosticoPublico),
            mediaGeral: calculatedMediaGeral,
          }
        }));
        const sortedList = list.sort((a, b) => a.nome.localeCompare(b.nome));
        setOrgs(sortedList);
      } catch (err) {
        console.error('Erro ao carregar organizações:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrgs();
  }, []);

  if (loading) return <Spinner text="Carregando organizações..." fullScreen />;


  return (
    <div className="public-org-showcase">
      <div className="showcase-search-container no-print" style={{
        maxWidth: '500px',
        margin: '0 auto 3rem auto',
        textAlign: 'left'
      }}>
        <label style={{
          display: 'block',
          fontSize: '0.9rem',
          fontWeight: '600',
          color: '#64748b',
          marginBottom: '0.75rem',
          textAlign: 'center'
        }}>
          Encontre uma organização específica:
        </label>
        <SearchableSelectBase
          options={orgs.map(o => ({ id: o.id, label: o.nome }))}
          onChange={(id) => navigate(`/diagnostico/${id}`)}
          placeholder="Digite o nome da instituição..."
          styles={{
            control: (base) => ({
              ...base,
              height: '52px',
              borderRadius: '14px',
              borderColor: '#e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              '&:hover': { borderColor: '#00A8E8' },
              backgroundColor: '#fff'
            })
          }}
        />
      </div>

      <div className="org-grid">
        {orgs.map((org) => (
          <div
            key={org.id}
            className={`org-card ${org.diagnosticoPublico ? 'enabled' : 'disabled'}`}
            role="button"
            tabIndex={org.diagnosticoPublico ? 0 : -1}
            aria-disabled={!org.diagnosticoPublico}
            onClick={() => {
              if (org.diagnosticoPublico) {
                navigate(`/diagnostico/${org.id}`);
              }
            }}
          >
            <div className="progress-ring-container">
              <svg className="progress-ring-svg" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle
                  className="progress-ring-background"
                  strokeWidth="8"
                  stroke="currentColor"
                  fill="transparent"
                  r="46"
                  cx="50"
                  cy="50"
                />
                {/* Progress circle */}
                <circle
                  className="progress-ring-circle"
                  strokeWidth="8"
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="transparent"
                  r="46"
                  cx="50"
                  cy="50"
                  style={{
                    strokeDasharray: 289.027,
                    strokeDashoffset: `calc(289.027 - (289.027 * ${org.mediaGeral || 0}) / 100)`
                  }}
                />
              </svg>
              {/* Percentage text */}
              <div className="progress-ring-text">
                {`${org.mediaGeral || '0.0'}%`}
              </div>
            </div>

            <div style={{ marginTop: '0.25rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'help', fontSize: '0.75rem' }} title="Este valor representa a média consolidada na última atualização diária. Para ver o valor em tempo real, acesse o Dashboard.">
              <FaInfoCircle style={{ fontSize: '0.7rem', color: '#94a3b8' }} />
              <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '500' }}>Atualização Diária</span>
            </div>
            <h3 className="org-name-new">{org.nome}</h3>
            <p className="org-subtitle">Índice de Maturidade</p>
            <span className="org-action">
              Ver Dashboard
              <i className="fas fa-arrow-right" style={{ marginLeft: '8px' }}></i>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PublicOrgShowcase;

